// NodeStats — compact CPU / RAM / Disk strip for one node, intended to sit
// inline with NodeCard on the Dashboard. Reuses the monitoring endpoint
// that already powers the full Monitoring page.
//
// Threshold bars (>90% red / >70% amber / <70% blue) are KEPT RAW: they are
// data-viz with ordinal thresholds, not generic UI badges.
import { useCurrentMetrics } from '@/hooks/useMonitoring'
import { Card } from '@/admin-design/_common'

function formatBytes(n: number): string {
  if (!n) return '0'
  const gb = n / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(1)}G`
  const mb = n / (1024 * 1024)
  return `${mb.toFixed(0)}M`
}

function Bar({
  label,
  used,
  total,
  valueText,
  unit,
}: {
  label: string
  used: number
  total: number
  valueText?: string
  unit?: string
}) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0
  const color =
    pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-blue-500'
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">
          {valueText ?? `${formatBytes(used)}/${formatBytes(total)}${unit ?? ''}`}
          <span className="ml-1 text-muted-foreground">({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export default function NodeStats({ nodeName }: { nodeName: string }) {
  const { data, isLoading, isError } = useCurrentMetrics()

  if (isLoading) {
    return (
      <Card className="p-3">
        <div className="space-y-2 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-4 bg-muted rounded" />
          ))}
        </div>
      </Card>
    )
  }

  const m = data?.find((n) => n.node_name === nodeName)?.system
  if (isError || !m || (!m.memory_total_bytes && !m.cpu_percent)) {
    return (
      <div className="border border-warning-soft rounded-lg bg-warning-soft p-2 text-[11px] text-warning-soft-foreground">
        Metrics unavailable for {nodeName}.
      </div>
    )
  }

  return (
    <Card className="p-3 space-y-2">
      <Bar
        label="CPU"
        used={m.cpu_percent}
        total={100}
        valueText={`${m.cpu_percent.toFixed(0)}%`}
        unit=""
      />
      <Bar
        label="RAM"
        used={m.memory_used_bytes}
        total={m.memory_total_bytes}
      />
      {m.disk_total_bytes > 0 && (
        <Bar
          label="Disk"
          used={m.disk_used_bytes}
          total={m.disk_total_bytes}
        />
      )}
    </Card>
  )
}
